// =============================================================================
// io/sync/archiveAccount.ts — archive and unarchive, one key, both directions.
// -----------------------------------------------------------------------------
// `archived` has no column. It rides `extras`, exactly like `excludeFromMetrics`
// and the cost fields — so this is a read-modify-write of a JSONB blob, not a
// column update, and the read is what makes it safe.
//
// ── 🚨 WHY IT IS NOT PART OF `saveAccount` ──
//
// `saveAccount` deliberately does not touch `archived` (D62): the edit form has
// no field for it, and a writer that sends a key its form does not own is how a
// value set on another surface gets reverted. That reasoning is unchanged — so
// archiving gets its own writer rather than a new flag on that one.
//
// ── ⚠ THE READ IS NOT OPTIONAL ──
//
// Writing `{ archived: true }` as the whole blob would erase `rulesX` (the two
// rules with no column), the three cost fields, `excludeFromMetrics`, and every
// key the shipping client writes that this one has never heard of. The stored
// blob is read first and spread under the one key being changed. `saveAccount`
// makes the same argument at length about a single spread being the entire
// mechanism.
//
// ── ⚠ ARCHIVING IS NOT DELETING, AND IT IS NOT "EXCLUDE FROM METRICS" ──
//
// An archived account still counts in every aggregate unless it is separately
// excluded. What archiving changes:
//
//   • it moves to the bottom of every account picker, greyed and badged
//   • the Accounts tab collapses it into its own group
//   • a RECURRING FEE STOPS ACCRUING — beta: "archive stops accrual"
//   • IMPORTS will not silently route into it; the commit gate prompts
//   • a realtime logger push whose only match is archived is left UNASSIGNED
//     rather than routed in
//
// That last pair is why the old client calls archiving "otherwise visual-only"
// and still guards it in four places. The word is about METRICS, not about
// consequences.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface ArchiveResult {
  ok: boolean;
  error: string | null;
  /** What `archived` is now. Null when the write did not happen. */
  archived: boolean | null;
  /** The extras blob as written, so a dry run can be read by a human. */
  extras?: Record<string, unknown>;
}

export interface ArchiveOptions {
  dryRun?: boolean;
}

/**
 * Set `archived` on one account.
 *
 * ⚠ TAKES THE TARGET STATE, NOT A TOGGLE. The old client flips whatever is in
 * its in-memory copy (`a.archived = !a.archived`), which is a race against
 * another tab: two clicks from two windows on a stale value can land on the
 * same result and one of them reported the opposite. An explicit `archived`
 * makes the request idempotent — pressing Archive twice archives, it does not
 * archive then unarchive.
 */
export async function setAccountArchived(
  sb: SupabaseClient,
  accountId: string,
  archived: boolean,
  opts: ArchiveOptions = {},
): Promise<ArchiveResult> {
  if (!accountId) {
    return { ok: false, error: 'Refusing to archive an account with no id.', archived: null };
  }

  // ── 1. The freshest extras, read now rather than when the tab loaded ──
  const { data, error: readError } = await sb
    .from('accounts')
    .select('extras')
    .eq('id', accountId)
    .maybeSingle();

  if (readError) {
    return {
      ok: false,
      error: reportFailureAs(
        'archive account', readError,
        `Couldn’t read the account before archiving: ${readError.message}`,
      ),
      archived: null,
    };
  }
  if (!data) {
    // ⚠ NOT a silent success. An UPDATE against a row RLS hides succeeds with an
    // empty result, so without this a deleted account would report "Archived."
    return {
      ok: false,
      error: 'That account no longer exists — reload and try again.',
      archived: null,
    };
  }

  const stored = (data.extras ?? {}) as Record<string, unknown>;
  // ⚠ THE SPREAD IS THE MECHANISM. See the header: everything else in the blob
  // must survive, including keys this client has never heard of.
  const extras = { ...stored, archived };

  if (opts.dryRun) return { ok: true, error: null, archived, extras };

  const { data: written, error } = await sb
    .from('accounts')
    .update({ extras })
    .eq('id', accountId)
    .select('id');

  if (error) {
    return {
      ok: false,
      error: reportFailureAs('archive account', error, error.message),
      archived: null,
      extras,
    };
  }
  if (!written || !written.length) {
    return {
      ok: false,
      error: 'That change matched no rows — nothing was written. Reload and try again.',
      archived: null,
      extras,
    };
  }
  return { ok: true, error: null, archived, extras };
}
